import React, { useState, useEffect } from 'react';
import { agendamentoService } from '../../services';
import type { Agendamento } from '../../types/entities';
import AgendamentoFormModal from '../modals/AgendamentoFormModal';

interface AgendaCardProps {
  className?: string;
}

const AgendaCard: React.FC<AgendaCardProps> = ({ className = '' }) => {
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgendamento, setSelectedAgendamento] = useState<Agendamento | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const loadAgendamentos = async () => {
    setLoading(true);
    try {
      const queryParams: any = {
        status: 'pendente,aberto', // Filtrar apenas pendentes e abertos
        limit: 10, // Limitar a 10 itens para o card
        page: 1
      };

      // Adicionar busca se houver termo
      if (searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }

      const response = await agendamentoService.list(queryParams);
      const data = response?.data || response || [];
      
      if (Array.isArray(data)) {
        setAgendamentos(data);
      } else if (data.items && Array.isArray(data.items)) {
        setAgendamentos(data.items);
      } else {
        setAgendamentos([]);
      }
    } catch (err: any) {
      setAgendamentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgendamentos();
  }, [searchTerm]);

  const handleAgendamentoClick = (agendamento: Agendamento) => {
    setSelectedAgendamento(agendamento);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedAgendamento(null);
  };

  const handleModalSuccess = () => {
    loadAgendamentos(); // Refresh list
    handleModalClose();
  };

  const handleNewAgendamento = () => {
    setSelectedAgendamento(null); // Clear selection for new agendamento
    setModalOpen(true);
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-4 ${expanded ? 'h-[450px]' : 'h-auto'} flex flex-col ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          Agenda
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
            {agendamentos.length}
          </span>
        </h2>
        <div className="flex gap-2">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <svg 
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button 
            onClick={handleNewAgendamento}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs transition-colors"
          >
            + Novo
          </button>
        </div>
      </div>

      {/* Conteúdo expansível */}
      {expanded && (
        <>
          {/* Campo de busca */}
          <div className="mb-3">
            <input
              type="text"
              placeholder="Buscar agendamentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-3 flex-1 overflow-y-auto max-h-[280px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            <span className="ml-2 text-gray-500 text-sm">Carregando...</span>
          </div>
        ) : agendamentos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-gray-400 text-4xl mb-2">📅</div>
            <p className="text-gray-500 text-sm">Nenhum agendamento encontrado</p>
            <p className="text-gray-400 text-xs mt-1">Clique em "Novo" para criar um agendamento</p>
          </div>
        ) : (
          agendamentos.map((agendamento: Agendamento) => (
            <div 
              key={agendamento.id} 
              className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-purple-50 hover:border-purple-200 transition-all duration-200 hover:shadow-sm"
              onClick={() => handleAgendamentoClick(agendamento)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {agendamento.descricaoAgendamento || 'Sem descrição'}
                    </p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      agendamento.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' : 
                      agendamento.status === 'aberto' ? 'bg-blue-100 text-blue-700' : 
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {agendamento.status || 'N/A'}
                    </span>
                  </div>
                  
                  {expanded && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Origem:</span> {agendamento.descricaoOrigem || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">De:</span> {agendamento.usuarioOrigem?.nome || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Para:</span> {agendamento.usuarioDestino?.nome || 'N/A'}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <span>🕒</span>
                    {agendamento.data ? new Date(agendamento.data).toLocaleString('pt-BR') : 'Sem data'}
                  </p>
                </div>
                
                <div className="ml-2 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                </div>
              </div>
            </div>
          ))
        )}
          </div>
        </>
      )}

      {/* Modal de Agendamento */}
      <AgendamentoFormModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        agendamento={selectedAgendamento || undefined}
        isEditing={!!selectedAgendamento}
      />
    </div>
  );
};

export default AgendaCard;
